/* eslint-disable indent */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Typography,
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import i18n from '../../i18';
import { EmptyWalletTransparentBulkIcon } from '../../icons-lib';
import SearchBox from '../../search-box/SearchBox';
import AssociatedBlockComponent from '../../components/associated-block/AssociatedBlockComponent';
import { fCurrency } from '../../utils/formatNumber';

const MultipleTopUpDialog = ({
    open,
    onClose,
    onTopUp,
    employees,
    context,
    userInfo
}) => {
    const [step, setStep] = useState(1);
    const [fromWallet, setFromWallet] = useState(null); // this will hold company object
    const [fromWalletId, setFromWalletId] = useState(null);
    const [totalAmount, setTotalAmount] = useState('');
    const [eachAmount, setEachAmount] = useState('');
    const [formError, setFormError] = useState('');
    const [lastEdited, setLastEdited] = useState(null); // 'total' | 'each'
    const [exchangeRate, setExchangeRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);
    const { server, controllersData } = context.getState();

    const isSmeAdmin = userInfo?.role?.systemRole === 'sme_admin';

    // Get pre-loaded SME companies data (same as create/edit forms)
    const smeCompanies = controllersData?.smeCompanies || [];
    const smeCompaniesTotalCount = controllersData?.smeCompaniesTotalCount || 0;

    useEffect(() => {
        const fetchSmeCompany = async () => {
            if (open && isSmeAdmin) {
                const company = await server.smeCompanies.getById(
                    userInfo.smeCompanyId
                );
                setFromWallet(company);
                setFromWalletId(company?.id);
            }
        };

        if (userInfo) {
            fetchSmeCompany();
        }
    }, [open, isSmeAdmin, server.smeCompanies, userInfo]);

    useEffect(() => {
        const fetchRate = async () => {
            if (!open) return;
            try {
                setRateLoading(true);
                const userCurrency = localStorage.currency || 'CHF';
                const resp = await server.currencyExchangeRates.getAll();
                const pair = resp?.data?.find(
                    (r) =>
                        r.currencyExchangePair?.fromCurrency?.shortName ===
                            'XAG' &&
                        r.currencyExchangePair?.toCurrency?.shortName ===
                            userCurrency
                );
                setExchangeRate(pair ? 1 / parseFloat(pair.rate) : null);
            } catch (e) {
                console.error('exchange rate fetch failed', e);
                setExchangeRate(null);
            } finally {
                setRateLoading(false);
            }
        };
        fetchRate();
    }, [open, server.currencyExchangeRates]);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            // Reset to initial state when dialog opens
            setStep(1);
            setTotalAmount('');
            setEachAmount('');
            setFormError('');
            setLastEdited(null);
            // Reset wallet selection for non-SME admins
            if (!isSmeAdmin) {
                setFromWallet(null);
                setFromWalletId(null);
            }
        }
    }, [open, isSmeAdmin]);

    useEffect(() => {
        if (totalAmount && employees.length > 0) {
            if (lastEdited === 'total') {
                const amt = parseFloat(totalAmount);
                if (!isNaN(amt)) {
                    const per = (amt / employees.length).toFixed(2);
                    setEachAmount(per);
                }
            }
        }
    }, [totalAmount, employees, lastEdited]);

    // Sync total when eachAmount edited
    useEffect(() => {
        if (eachAmount && employees.length > 0) {
            if (lastEdited === 'each') {
                const per = parseFloat(eachAmount);
                if (!isNaN(per)) {
                    const tot = (per * employees.length).toFixed(2);
                    setTotalAmount(tot);
                }
            }
        }
    }, [eachAmount, employees, lastEdited]);

    const handleNext = () => {
        // basic validation
        if (!fromWallet) {
            setFormError(i18n.t('errors.selectWallet'));
            return;
        }
        const total = parseFloat(totalAmount);
        if (isNaN(total) || total <= 0) {
            setFormError(i18n.t('errors.enterAmount'));
            return;
        }
        const convertedTotal = total * exchangeRate;
        const balance = parseFloat(fromWallet?.wallet?.currentBalance || 0);
        if (convertedTotal > balance) {
            setFormError(i18n.t('errors.insufficientBalance'));
            return;
        }

        const perEmployee = (total / employees.length).toFixed(2);
        setEachAmount(perEmployee);
        setFormError('');
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
    };

    const handleSubmit = () => {
        if (!exchangeRate) return;
        const topUpData = {
            fromWallet,
            totalAmount: totalAmount * exchangeRate,
            eachAmount: eachAmount * exchangeRate,
            employees
        };
        onTopUp(topUpData);
    };

    if (!userInfo) {
        return null;
    }

    const renderStepOne = () => (
        <>
            <DialogTitle>{i18n.t('dialogs.multipleTopUp.title')}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        {/* From Wallet Header */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                        >
                            <Typography variant="h6">
                                {i18n.t('dialogs.topUp.fromWallet')}
                            </Typography>
                            {fromWallet?.wallet?.walletNumber && (
                                <Typography
                                    variant="subtitle1"
                                    color="textSecondary"
                                >
                                    {fromWallet.wallet.walletNumber}
                                </Typography>
                            )}
                        </Box>

                        {/* From wallet selector / chip */}
                        {!fromWallet ? (
                            <SearchBox
                                serverSearch
                                searchFields={['name', 'email']}
                                searchController="smeCompanies"
                                handleChange={(val) => {
                                    setFromWalletId(val);
                                    if (val) {
                                        const comp = smeCompanies.find(
                                            (c) => c.id === val
                                        );
                                        if (comp) setFromWallet(comp);
                                    }
                                }}
                                value={fromWalletId}
                                multiple={false}
                                context={context}
                                icon={<EmptyWalletTransparentBulkIcon />}
                                translatedLabel={i18n.t(
                                    'buttons.chooseSmeAccount'
                                )}
                                translatedSearchLabel={i18n.t(
                                    'pages.smeCompanies.title'
                                )}
                                primaryTitle={i18n.t('columns.smeName')}
                                secondaryTitle={i18n.t('columns.email')}
                                primaryKey="name"
                                secondaryKey="email"
                                items={smeCompanies}
                                totalItemsCount={smeCompaniesTotalCount}
                                saveFullData={false}
                                disabled={isSmeAdmin}
                            />
                        ) : (
                            <AssociatedBlockComponent
                                icon={<EmptyWalletTransparentBulkIcon />}
                                primaryTitle={i18n.t('columns.account')}
                                primaryContent={fromWallet.name}
                                secondaryTitle={i18n.t('columns.balance')}
                                secondaryContent={`${fCurrency(
                                    fromWallet?.wallet?.currentBalance || 0
                                )} SILbar`}
                                hideSecondaryLabel={false}
                                action={{
                                    label: i18n.t('buttons.change'),
                                    onClick: () => {
                                        setFromWallet(null);
                                        setFromWalletId(null);
                                    }
                                }}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={i18n.t(
                                'dialogs.multipleTopUp.totalTopUpAmount'
                            )}
                            type="number"
                            value={totalAmount}
                            onChange={(e) => {
                                setLastEdited('total');
                                setTotalAmount(e.target.value);
                            }}
                            inputProps={{ step: '0.01' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                color: '#BA9B61'
                                            }}
                                        >
                                            {localStorage.currency || 'CHF'}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {totalAmount && !isNaN(parseFloat(totalAmount)) && (
                            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                                {rateLoading ? (
                                    <CircularProgress size={12} />
                                ) : exchangeRate ? (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#98A2B3' }}
                                    >
                                        {`≈ ${(
                                            parseFloat(totalAmount) *
                                            exchangeRate
                                        ).toFixed(2)} SILbar`}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={i18n.t(
                                'dialogs.multipleTopUp.topUpAmountForEach'
                            )}
                            type="number"
                            value={eachAmount}
                            onChange={(e) => {
                                setLastEdited('each');
                                setEachAmount(e.target.value);
                            }}
                            inputProps={{ step: '0.01' }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                color: '#BA9B61'
                                            }}
                                        >
                                            {localStorage.currency || 'CHF'}
                                        </Typography>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {eachAmount && !isNaN(parseFloat(eachAmount)) && (
                            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                                {rateLoading ? (
                                    <CircularProgress size={12} />
                                ) : exchangeRate ? (
                                    <Typography
                                        variant="caption"
                                        sx={{ color: '#98A2B3' }}
                                    >
                                        {`≈ ${(
                                            parseFloat(eachAmount) *
                                            exchangeRate
                                        ).toFixed(2)} SILbar`}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                    </Grid>
                </Grid>
                {formError && (
                    <Typography color="error" sx={{ mt: 2 }}>
                        {formError}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{i18n.t('buttons.cancel')}</Button>
                <Button onClick={handleNext} variant="contained">
                    {i18n.t('buttons.proceed')}
                </Button>
            </DialogActions>
        </>
    );

    const renderStepTwo = () => (
        <>
            <DialogTitle>{i18n.t('dialogs.confirmationTitle')}</DialogTitle>
            <DialogContent>
                {/* Chips row */}
                <Box display="flex" gap={2} mb={3}>
                    <AssociatedBlockComponent
                        icon={<EmptyWalletTransparentBulkIcon />}
                        primaryTitle={i18n.t('dialogs.topUp.fromWallet')}
                        primaryContent={fromWallet?.name || ''}
                        secondaryTitle={i18n.t('columns.walletNumber')}
                        secondaryContent={
                            fromWallet?.wallet?.walletNumber || ''
                        }
                        hideSecondaryLabel={false}
                    />
                    <AssociatedBlockComponent
                        primaryTitle={i18n.t(
                            'dialogs.multipleTopUp.totalTopUpAmount'
                        )}
                        primaryContent={`${fCurrency(totalAmount)} ${
                            localStorage.currency || 'CHF'
                        }${
                            exchangeRate
                                ? ` ≈ ${fCurrency(
                                      parseFloat(totalAmount) * exchangeRate
                                  )} SILbar`
                                : ''
                        }`}
                        hideSecondaryLabel={true}
                    />
                </Box>

                {/* Employee list table */}
                <Typography variant="h6" gutterBottom>
                    {i18n.t('dialogs.topUp.toWallet')}
                </Typography>
                <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ maxHeight: 400 }}
                >
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {i18n.t('columns.walletNumber')}
                                </TableCell>
                                <TableCell>
                                    {i18n.t('columns.employeeName')}
                                </TableCell>
                                <TableCell>{i18n.t('columns.email')}</TableCell>
                                <TableCell align="right">
                                    {i18n.t('dialogs.topUp.amountLabel')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {employees.map((employee) => (
                                <TableRow key={employee.id} hover>
                                    <TableCell>
                                        {employee.wallet?.walletNumber || ''}
                                    </TableCell>
                                    <TableCell>{employee.firstName}</TableCell>
                                    <TableCell>{employee.email}</TableCell>
                                    <TableCell align="right">
                                        {`${fCurrency(eachAmount)} ${
                                            localStorage.currency || 'CHF'
                                        }${
                                            exchangeRate
                                                ? ` ≈ ${fCurrency(
                                                      parseFloat(eachAmount) *
                                                          exchangeRate
                                                  )} SILbar`
                                                : ''
                                        }`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleBack}>{i18n.t('buttons.back')}</Button>
                <Button onClick={handleSubmit} variant="contained">
                    {i18n.t('buttons.confirmTopUp')}
                </Button>
            </DialogActions>
        </>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            {step === 1 ? renderStepOne() : renderStepTwo()}
        </Dialog>
    );
};

export default MultipleTopUpDialog;
